import { computed, action, makeObservable } from 'mobx';
import { getErrorByCode } from 'src/domains/layouts/webview/components/common/errorMessage/errors';
import { HeaderNavigationLinksState } from 'src/domains/players/state/specialEventsState/HeaderNavigationLink';
import { SignUpState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/SignUp.state';
import { CreateAccountStep } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/createAccount/CreateAccount.state';
import { StarLoginState } from 'src/domains/players/state/starLoginState/StarLoginState';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { LandingPage } from './LandingPage';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/domains/common/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';

export class SpecialEvents {
    private readonly router: StarRouter;
    private readonly signUpState: SignUpState;
    private readonly loginState: StarLoginState;
    private readonly accountState: AccountState;
    private readonly googleTagManager: GoogleTagManagerState;
    public readonly headerNavigationLinks: HeaderNavigationLinksState;
    private readonly landingPageMap: MobxMapAutoNew<string | null, LandingPage>;
    public constructor(common: Common) {
        makeObservable(this);
        this.router = StarRouter.get(common);
        this.signUpState = SignUpState.get(common);
        this.loginState = StarLoginState.get(common);
        this.accountState = AccountState.get(common);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.landingPageMap = new MobxMapAutoNew((promotionSlug: string | null) => {
            return new LandingPage(this.router, this.signUpState, promotionSlug, common);
        });

        this.headerNavigationLinks = new HeaderNavigationLinksState(common);
    }

    public static get = AutoWeakMap.create((common: Common) => new SpecialEvents(common));

    public getLandingPageState = (promotionSlug: string): LandingPage => {
        return this.landingPageMap.get(promotionSlug);
    };

    @action public logIn = (promotionSlug: string): void => {
        const result = this.loginState.loginFormModel.result;
        this.loginState.loginFormModel.setAsVisited();
        if (result.value.type === 'ok') {
            const data = {
                email: result.value.data.email,
                password: result.value.data.password,
            };

            (async (): Promise<void> => {
                const response = await this.accountState.loginUser(data.email, data.password);
                switch (response.type) {
                    case 'CreateSessionResponseOk':
                        this.loginState.loginFormModel.reset();
                        this.router.closeAccount();
                        this.getLandingPageState(promotionSlug).displayLP = false;
                        break;
                    case 'CreateSessionResponseErrors':
                        this.loginState.setLoginErrorMsg(response.error_description);
                        break;
                    case 'CreateSessionResponseErrorAccess':
                        this.loginState.verifyAccountState.setEmail(data.email);
                        this.router.redirectToVerifyAccount();

                        break;
                }
            })().catch((err) => {
                console.error(err);
                this.loginState.setLoginErrorMsg(getErrorByCode('ERROR_UNKNOWN'));
            });
        } else {
            console.log(result);
            this.loginState.setLoginErrorMsg(result.errors()[0] ?? '');
        }
    };

    @computed public get promoSignUpData(): CreateAccountStep | null {
        if (this.signUpState.currentView.step.type === 'create-account') {
            return this.signUpState.currentView.step.state;
        }

        return null;
    }

    @action public handleRedirectToNewSignup = (promotionSlug: string): void => {
        if (this.promoSignUpData !== null) {
            const promoCode = this.getLandingPageState(promotionSlug).promoCode;
            const date = DateTime.current().utc().format('YYYY-MM-DD HH:mm:ss [GMT]');
            this.googleTagManager.gtmSignUpStepOne(promoCode, date);

            this.signUpState.currentView.redirectToCreateAccount('your-details', {
                promoCode: promoCode,
                email: this.promoSignUpData.welcomeState.emailState.value,
                password: this.promoSignUpData.welcomeState.passwordState.value,
            });
        }

        this.router.redirectToSignUp();
        this.getLandingPageState(promotionSlug).displayLP = false;
    };

    // ------------------------------- new end

    @action public closePromoTermsConditions = (): void => {
        this.router.closeAccount();
    };
}
